<template>
  <div
    :id="id"
    :class="[{ 'dark': dark }, size]"
    class="vue-phone-number-input"
  >
    <label v-if="label" class="vs-input--label" :for="label">{{ label }}</label>

    <div class="flex">
      <div v-if="!noCountrySelector" class="select-country-container">
        <CountrySelector
          :id="`${uniqueId}_country_selector`"
          ref="CountrySelector"
          v-model="countryCode"
          :countries-height="countriesHeight"
          :dark="dark"
          :disabled="disabled"
          :error="shouldChooseCountry"
          :ignored-countries="ignoredCountries"
          :items="codesCountries"
          :label="t.countrySelectorLabel"
          :no-flags="noFlags"
          :only-countries="onlyCountries"
          :preferred-countries="preferredCountries"
          :show-code-on-list="showCodeOnList"
          :size="size"
          :theme="theme"
          :valid="isValid && !noValidatorState"
          @open="$emit('onOpen')"
          @close="$emit('onClose')"
          class="input-country-selector"
        >
          <slot slot="arrow" name="arrow"/>
        </CountrySelector>
      </div>
      <div class="flex-1">
        <InputTel
          :id="`${uniqueId}_phone_number`"
          ref="PhoneNumberInput"
          v-model="phoneNumber"
          :dark="dark"
          :disabled="disabled || !countryCode"
          :error="error"
          :label="t.phoneNumberLabel"
          :no-country-selector="noCountrySelector"
          :required="required"
          :size="size"
          :theme="theme"
          :valid="isValid && !noValidatorState"
          class="input-phone-number"
          v-bind="$attrs"
          @blur="$emit('phone-number-blur')"
          @focus="$emit('phone-number-focused')"
          @keydown="(e) => { lastKeyPressed = e.keyCode }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {countries, countriesIso} from './assets/js/phoneCodeCountries.js'
import {parsePhoneNumberFromString, AsYouType} from 'libphonenumber-js'
import InputTel from './InputTel'
import CountrySelector from './CountrySelector'
import locales from './assets/locales'

const getShadowColor = (color) => {
  return color
}

const browserLocale = () => {
  if (!window) return null
  const browserLocale = window.navigator.userLanguage || window.navigator.language
  let locale = browserLocale ? browserLocale.substr(3, 4).toUpperCase() : null
  if (locale === '') locale = browserLocale.substr(0, 2).toUpperCase()
  return locale
}

const isCountryAvailable = (locale) => {
  return countriesIso.includes(locale)
}

export default {
  name: 'vx-input-phone',
  components: { InputTel, CountrySelector },
  props: {
    value: {type: String, default: null},
    id: {type: String, default: 'MazPhoneNumberInput'},
    color: {type: String, default: 'dodgerblue'},
    validColor: {type: String, default: 'primary'},
    errorColor: {type: String, default: 'danger'},
    darkColor: {type: String, default: '#424242'},
    disabled: {type: Boolean, default: false},
    defaultCountryCode: {type: String, default: null},
    size: {type: String, default: null},
    label: {type: String, default: null},
    preferredCountries: {type: Array, default: null},
    onlyCountries: {type: Array, default: null},
    ignoredCountries: {type: Array, default: Array},
    translations: {type: Object, default: null},
    noValidatorState: {type: Boolean, default: false},
    noFlags: {type: Boolean, default: false},
    error: {type: Boolean, default: false},
    noExample: {type: Boolean, default: false},
    required: {type: Boolean, default: false},
    countriesHeight: {type: Number, default: 30},
    noUseBrowserLocale: {type: Boolean, default: false},
    fetchCountry: {type: Boolean, default: false},
    noCountrySelector: {type: Boolean, default: false},
    showCodeOnList: {type: Boolean, default: true},
    dark: {type: Boolean, default: false},
    borderRadius: {type: Number, default: 4},
    reload: {type: Boolean, default: false}
  },
  data() {
    return {
      results: {},
      lastKeyPressed: null,
      removedCountry: false,
      userLocale: this.defaultCountryCode,
    }
  },
  computed: {
    uniqueId() {
      return `${this.id}-${this._uid}`
    },
    t() {
      return {
        ...locales,
        ...this.translations
      }
    },
    codesCountries() {
      return countries
    },
    countryCode: {
      get() {
        return this.userLocale || this.results.countryCode
      },
      set(newCountry) {
        this.setLocale(newCountry)
        this.$refs.PhoneNumberInput.$el.querySelector('input').focus()
      }
    },
    phoneNumber: {
      get() {
        return this.value
      },
      set(newPhone) {
        this.emitValues({countryCode: this.countryCode, phoneNumber: newPhone})
      }
    },
    shouldChooseCountry() {
      return !this.countryCode && !!this.phoneNumber
    },
    isValid() {
      return this.results.isValid
    },
    theme() {
      return {
        colorValue: this.color,
        color: {color: this.color},
        textColor: {color: '#747474'},
        textDarkColor: {color: 'rgba(255, 255, 255, 0.7)'},
        validColor: {color: this.validColor},
        errorColor: {color: this.errorColor},
        darkColor: {color: this.darkColor},
        bgColor: {backgroundColor: this.color},
        bgValidColor: {backgroundColor: this.validColor},
        bgErrorColor: {backgroundColor: this.errorColor},
        bgDarkColor: {backgroundColor: this.darkColor},
        borderColor: {borderColor: this.color},
        borderValidColor: {borderColor: this.validColor},
        borderErrorColor: {borderColor: this.errorColor},
        borderDarkColor: {borderColor: this.darkColor},
        boxShadowColor: {boxShadow: `0 0 0 0.125rem ${getShadowColor(this.color)}`},
        boxShadowValid: {boxShadow: `0 0 0 0.125rem ${getShadowColor(this.validColor)}`},
        boxShadowError: {boxShadow: `0 0 0 0.125rem ${getShadowColor(this.errorColor)}`},
        borderRadius: {borderRadius: `${this.borderRadius}px`},
        borderLeftRadius: {
          borderTopLeftRadius: `${this.borderRadius}px`,
          borderBottomLeftRadius: `${this.borderRadius}px`
        },
        borderRightRadius: {
          borderTopRightRadius: `${this.borderRadius}px`,
          borderBottomRightRadius: `${this.borderRadius}px`
        }
      }
    }
  },
  watch: {
    reload(){
      this.resetValues();
    },
    defaultCountryCode(newValue, oldValue) {
      if (newValue === oldValue) return
      this.setLocale(newValue)
    },
    phoneNumber: {
      handler(newValue, oldValue) {
        let parsedOld = null;
        let parsedNew = newValue.toString().replace(/[^0-9.]+/gi, '');
        if (oldValue) parsedOld = oldValue.toString().replace(/[^0-9.]+/gi, '');

        if ( !parsedOld || ( parsedNew && parsedOld && (
            !parsedOld.includes(parsedNew) || (parsedOld.includes(parsedNew) && parsedOld.length !== parsedNew.length && !this.removedCountry)
        ))) {
          if ( !this.removedCountry ){
            parsedNew = '+' + parsedNew
          }

          const phoneNumber = parsePhoneNumberFromString(parsedNew)
          if (phoneNumber) {
            this.emitValues({
              phoneNumber: phoneNumber.nationalNumber,
              countryCode: this.countryCode ? this.countryCode : phoneNumber.country
            })
          }
        } else if (parsedNew) {
          this.removedCountry = true
        }
      },
      immediate: true
    }
  },
  async mounted() {
    try {
      this.resetValues();

      if (this.phoneNumber && this.defaultCountryCode) this.emitValues({countryCode: this.defaultCountryCode, phoneNumber: this.phoneNumber})

      if (this.defaultCountryCode) return

      !this.noUseBrowserLocale
        ? this.setLocale(browserLocale())
        : null
    } catch (err) {
      throw new Error(err)
    }
  },
  methods: {
    getAsYouTypeFormat(payload) {
      const {countryCode, phoneNumber} = payload
      const asYouType = new AsYouType(countryCode)
      return phoneNumber ? asYouType.input(phoneNumber) : null
    },
    getParsePhoneNumberFromString({phoneNumber, countryCode}) {
      this.removedCountry = true;
      const parsing = phoneNumber && countryCode ? parsePhoneNumberFromString(phoneNumber, countryCode) : null
      return {
        countryCode: countryCode,
        isValid: false,
        ...(phoneNumber && (phoneNumber !== '') ? { phoneNumber: phoneNumber } : null),
        ...(parsing ? {
            countryCallingCode: parsing.countryCallingCode,
            formattedNumber: parsing.number,
            nationalNumber: parsing.nationalNumber,
            isValid: parsing.isValid(),
            type: parsing.getType(),
            formatInternational: parsing.formatInternational(),
            formatNational: parsing.formatNational(),
            uri: parsing.getURI(),
            e164: parsing.format('E.164')
          } : null
        )
      }
    },
    emitValues(payload) {
      let asYouType = this.getAsYouTypeFormat(payload)
      const backSpacePressed = this.lastKeyPressed === 8

      this.$nextTick(() => {
        const lastCharacOfPhoneNumber = this.phoneNumber ? this.phoneNumber.trim().slice(-1) : false
        if (backSpacePressed && lastCharacOfPhoneNumber && (lastCharacOfPhoneNumber.slice(-1) === ')')) {
          asYouType = this.phoneNumber.slice(0, -2)
          payload.phoneNumber = this.phoneNumber.slice(0, -2)
        }

        this.results = this.getParsePhoneNumberFromString(payload)
        this.$emit('update', this.results)
        this.$emit('input', asYouType)
      })
    },
    setLocale(locale) {
      const countryAvailable = isCountryAvailable(locale)
      if (countryAvailable && locale) {
        this.userLocale = countryAvailable ? locale : null
        this.emitValues({countryCode: locale, phoneNumber: this.phoneNumber})
      }
    },
    resetValues(){
      this.results = {};
      this.userLocale = null;
      this.removedCountry = false;
    }
  }
}
</script>
<style lang="scss" scoped>
.vue-phone-number-input {
  .select-country-container {
    flex: 0 0 120px;
    width: 30%;
    min-width: 30%;
    max-width: 30%;
  }

  &.sm .select-country-container {
    flex: 0 0 110px;
    width: 110px;
    min-width: 110px;
    max-width: 110px;
  }

  &.lg .select-country-container {
    flex: 0 0 130px;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
}
</style>